import { useEffect, useState } from 'react';
import { 
  Routes, 
  Route, 
  Navigate,
  Outlet, 
} from 'react-router-dom';
import { CircularProgress, CssBaseline, Grid, useMediaQuery } from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { useDispatch } from 'react-redux';
import AppNavigation from './components/AppNavigation';
import Profile from './registeredViews/Profile';
import Support from './registeredViews/Support';
import Admin from './registeredViews/Admin';
import useCurrentUserInfo from './hooks/useCurrentUserInfo';
import { saveUserInfo } from './redux/actions/userActions';
import { AppContainer } from './globalStyles/styled';
import AddRecipePage from './registeredViews/AddRecipePage';
import AddVideoPage from './registeredViews/AddVideoPage';
import ProfileInfoControl from './containers/AdminPanel/TabPanels/ProfileInfoControl';
import RecipeLibraryControl from './containers/AdminPanel/TabPanels/RecipeLibraryControl';
import VideoLibraryControl from './containers/AdminPanel/TabPanels/VideoLibraryControl';
import MyStatsControl from './containers/AdminPanel/TabPanels/MyStatsControl';
import EditProfilePage from './registeredViews/EditProfilePage';
import EditRecipe from './registeredViews/EditRecipe';
import EditVideo from './registeredViews/EditVideo';
import ChefPreviewPage from './registeredViews/ChefPreviewPage';
import Landing from './unregisteredViews/Landing';
import TutorialPage from './registeredViews/TutorialPage';
import { chefAdminGetRecipesAndVideos } from './redux/actions/chefAdminActions';

function App() {

	const domain = `${process.env.REACT_APP_AUTH0_DOMAIN}`;
  
	const { 
		user, 
		getAccessTokenSilently,
    isLoading,
    isAuthenticated 
	} = useAuth0();
  const dispatch = useDispatch();
  const userInfo = useCurrentUserInfo();

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
	const drawerWidth = isMobile ? '75%' : 200;

  const [authCheckCompleted, setAuthCheckCompleted] = useState(false);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(isMobile ? false : true);

  console.log(
    '%c Go away, nothing to see here ;)', 
    'background: #66a18c; color: white; font-size: 20px;'
  );

  const PrivateRoutes = () => {
    if (isLoading || (!authCheckCompleted && isAuthenticated)) {
      return (
        <Grid
          sx={{
            height: '100dvh',
            width: '90dvw',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <CircularProgress
            sx={{
              height: '5rem',
              width: '5rem',
              color: '#84A8FF'
            }}
          />
        </Grid>
      );
    }

    return isAuthenticated ? <Outlet /> : <Navigate to="/" />;
  };

  useEffect(() => {
    const getUserMetadata = async () => {
      if (!user || isLoadingMetadata) return;

      setIsLoadingMetadata(true);
      try {
        const accessToken = await getAccessTokenSilently({
          authorizationParams: {
            audience: `https://${domain}/api/v2/`,
            scope: "read:current_user",
          },
        });

        const userDetailsByIdUrl = `https://${domain}/api/v2/users/${user.sub}`;
        const metadataResponse = await fetch(userDetailsByIdUrl, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!metadataResponse.ok) {
          throw new Error('Failed to fetch user metadata');
        }

        const user_metadata = await metadataResponse.json();
        
        if(user_metadata){
          dispatch(saveUserInfo(user_metadata, dispatch));
        }
      } catch (error) {
        console.error('Error fetching user metadata:', error);
      } finally {
        setIsLoadingMetadata(false);
      }
    };

    if (isAuthenticated && user) {
      getUserMetadata();
    }
  }, [user, isAuthenticated, getAccessTokenSilently, dispatch]);

  useEffect(() => {
    if (!isLoading) {
      if (!isAuthenticated || (user && userInfo?.user_id) || isLoadingMetadata === false) {
        dispatch(
          chefAdminGetRecipesAndVideos(userInfo?.user_id)
        );
        setAuthCheckCompleted(true);
      } else {
        setAuthCheckCompleted(false);
      }
    }
  }, [isLoading, isAuthenticated, user, userInfo, isLoadingMetadata]);
  
  return (
    <>
      <AppContainer>
			  <CssBaseline />
        <AppNavigation 
          setDrawerOpen={setDrawerOpen} 
          drawerOpen={drawerOpen} 
          drawerWidth={drawerWidth}
        />
        <Routes>

          <Route path = "/" element={<Landing />} />

          {/* <Route   path = "/demo"                         element = { <LiveDemo      drawerOpen={drawerOpen} drawerWidth={drawerWidth} />  }  >
          </Route> */}

          <Route element  =  { <PrivateRoutes /> }>
            <Route path = "/support"                      element = { <Support         drawerOpen={drawerOpen} drawerWidth={drawerWidth} /> } />
            <Route path = "/profile/:slug"                element = { <Profile         drawerOpen={drawerOpen} drawerWidth={drawerWidth} /> } />
            <Route path = "/add-recipe"                   element = { <AddRecipePage   drawerOpen={drawerOpen} drawerWidth={drawerWidth} /> } />            
            <Route path = "/add-video"                    element = { <AddVideoPage    drawerOpen={drawerOpen} drawerWidth={drawerWidth} /> } />   
            <Route path = "/edit-profile-info"            element = { <EditProfilePage drawerOpen={drawerOpen} drawerWidth={drawerWidth} /> } />   
            <Route path = "/edit-recipe-details/:id"      element = { <EditRecipe      drawerOpen={drawerOpen} drawerWidth={drawerWidth} /> } />   
            <Route path = "/edit-video-details"           element = { <EditVideo       drawerOpen={drawerOpen} drawerWidth={drawerWidth} /> } />   
            <Route path = "/tutorial"                     element = { <TutorialPage    drawerOpen={drawerOpen} drawerWidth={drawerWidth} /> } />   
            <Route path = "/chef-page-preview"            element = { <ChefPreviewPage drawerOpen={drawerOpen} drawerWidth={drawerWidth} /> } >   
              <Route index element={<Navigate to="aisous-chef" replace />} />
              <Route path="aisous-chef" element={<ProfileInfoControl />} />
              <Route path="library-chef" element={<RecipeLibraryControl />} />
              <Route path="featured-chef" element={<VideoLibraryControl />} />
              <Route path="videos-chef" element={<VideoLibraryControl />} />
              <Route path="profile-chef" element={<MyStatsControl />} />
            </Route>   
            <Route path="/admin" element={<Admin drawerOpen={drawerOpen} drawerWidth={drawerWidth} />}>
              <Route index element={<Navigate to="profile" replace />} />
              <Route path="profile" element={<ProfileInfoControl />} />
              <Route path="library" element={<RecipeLibraryControl />} />
              <Route path="videos" element={<VideoLibraryControl />} />
              <Route path="stats" element={<MyStatsControl />} />
            </Route>         
          </Route>        
        </Routes>
      </AppContainer>
    </>
  );
};

export default App;
